import React from 'react';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import { openApiFetchPost } from '../../../store/openApiFetch';
import { getFailureCause, getFailureOrigin } from '../../../utils/data/fmecaDataDefinition';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    PanelBody,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { showSavedToast } from '../../utils/toastMessages';
import { TotalRiskScoreDot } from '../../utils/TotalRiskScoreDot';
import { SurveyFmecaNenMaintenanceMeasures } from '../Common/SurveyFmecaNenMaintenanceMeasures';
import { IFailureMode } from '../Interfaces/IFailureMode';
import { MaintenanceFailureModeEdit } from './FailureModeMaintenanceEdit';
import { getUnitFailureModesToCopy } from './getUnitFailureModesToCopy';
import { SurveyEditCopyFailureMode } from './SurveyEditCopyFailureMode';
import { SurveyEditFmecaFurtherInvestigations } from './SurveyEditFmecaFurtherInvestigations';

interface ISurveyMaintenanceProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: string | undefined;
    unitId?: string | undefined;
    manifestationId?: string | undefined;
    failureModeId?: string | undefined;
    selectedName?: string | undefined;
    selectedFailureModeUpdate: (selectedFailureModeId?: string) => void;
    isCraAvailable: boolean;
    isFmecaAvailable: boolean;
}

interface ISurveyMaintenanceState {
    failureModes: IFailureMode[];
    showAdd: boolean;
    showEdit: boolean;
    selectedFailureModeId?: undefined | string;
    unitFailureModes: IFailureMode[];
    shouldShowCopyFailureModeButton: boolean;
    showCopy: boolean;
}

class SurveyMaintenanceComponent extends ComponentSetStatePromised<ISurveyMaintenanceProps, ISurveyMaintenanceState> {
    constructor(props: ISurveyMaintenanceProps) {
        super(props);

        this.state = {
            failureModes: [],
            showAdd: false,
            showEdit: false,
            unitFailureModes: [],
            showCopy: false,
            shouldShowCopyFailureModeButton: false,
        };
    }

    public override async componentDidMount() {
        await this.loadFailureModes();
        await this.selectFailureMode(this.props.failureModeId);
        await this.loadUnitFailureModes();
    }

    public override async componentDidUpdate(prevProps: ISurveyMaintenanceProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ selectedFailureModeId: undefined, showAdd: false, showEdit: false });
            await this.loadFailureModes();
            await this.loadUnitFailureModes();
        }
    }

    public override render() {
        return (
            <Tray>
                <EditPanel bottomMargin={true} noPadding={true}>
                    {this.props.isFmecaAvailable && (
                        <>
                            <CollapsiblePanel heading={{ dark: true, text: this.getHeaderFailureList() }}>
                                <PanelBody isForm={true}>
                                    <PanelTable hover={true}>
                                        <TableHead>
                                            <TableHeadRow>
                                                <TableHeader>
                                                    {this.props.t('common.unit')}/{this.props.t('common.manifestation')}
                                                </TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureMode')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureCause')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.failureOrigin')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.analysisRiskScoreTableHeader')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.verificationRiskScoreTableHeader')}</TableHeader>
                                                <TableHeader>{this.props.t('failureMode.maintenanceRiskScoreTableHeader')}</TableHeader>
                                            </TableHeadRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.state.failureModes.map((failureMode) => (
                                                <TableRow
                                                    key={failureMode.id}
                                                    isRowSelected={failureMode.id === this.state.selectedFailureModeId}
                                                    onClick={async () => this.selectFailureMode(failureMode.id)}
                                                >
                                                    <TableData>
                                                        {failureMode.unit.name}{' '}
                                                        {failureMode.manifestation.name ? ` - ${failureMode.manifestation.name}` : ''}
                                                    </TableData>
                                                    <TableData>
                                                        {failureMode.defaultFailureMode.name
                                                            ? failureMode.defaultFailureMode.name
                                                            : failureMode.customName}
                                                    </TableData>
                                                    <TableData>{getFailureCause(failureMode.metaData)}</TableData>
                                                    <TableData>{getFailureOrigin(failureMode.metaData)}</TableData>
                                                    <TableData>
                                                        <TotalRiskScoreDot value={failureMode.analysisRamsWeightedPriority} />{' '}
                                                    </TableData>
                                                    <TableData>
                                                        <TotalRiskScoreDot value={failureMode.verificationRamsWeightedPriority} />{' '}
                                                    </TableData>
                                                    <TableData>
                                                        <TotalRiskScoreDot value={failureMode.maintenanceRamsWeightedPriority} />{' '}
                                                    </TableData>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </PanelTable>

                                    <PanelButtons>
                                        {this.state.shouldShowCopyFailureModeButton && (
                                            <EditPanelButton
                                                text={this.props.t('failureMode.copyFromUnit')}
                                                onClick={async () => this.showCopyFailureMode()}
                                                small
                                            />
                                        )}
                                    </PanelButtons>

                                    {this.state.showCopy && this.props.manifestationId && (
                                        <SurveyEditCopyFailureMode
                                            unitFailureModes={this.state.unitFailureModes}
                                            surveyId={this.props.surveyId}
                                            manifestationId={this.props.manifestationId}
                                            onCancel={() => this.setState({ showCopy: false })}
                                            onSaved={async () => this.failureModeSaved('add')}
                                        />
                                    )}

                                    {this.state.showEdit && this.state.selectedFailureModeId && (
                                        <MaintenanceFailureModeEdit
                                            onCancel={() => this.setState({ showEdit: false })}
                                            onSaved={async () => this.failureModeSaved('edit')}
                                            surveyId={this.props.surveyId}
                                            objectId={this.props.objectId}
                                            elementId={this.props.elementId}
                                            unitId={this.props.unitId}
                                            manifestationId={this.props.manifestationId}
                                            failureModeId={this.state.selectedFailureModeId}
                                            onRemove={async (id) => this.remove(id)}
                                        />
                                    )}
                                </PanelBody>
                            </CollapsiblePanel>
                        </>
                    )}
                </EditPanel>
                {((this.props.unitId && !this.props.manifestationId) || this.state.selectedFailureModeId) && (
                    <div className="mb20">
                        <CollapsiblePanel
                            heading={{
                                dark: true,
                                text: this.getHeaderList(this.props.t('maintenanceMeasure.maintenanceMeasures')),
                                initialIsClosed: true,
                            }}
                        >
                            <PanelBody isForm={true}>
                                <SurveyFmecaNenMaintenanceMeasures
                                    surveyId={this.props.surveyId}
                                    unitId={this.getUnitIdForMaintenanceMeasures()}
                                    inspectionStandardType={InspectionStandardTypes.Fmeca}
                                    failureModeId={this.state.selectedFailureModeId}
                                    surveyIsFmecaAvailable={this.props.isFmecaAvailable}
                                />
                            </PanelBody>
                        </CollapsiblePanel>
                    </div>
                )}

                {(this.props.unitId || this.props.manifestationId || this.state.selectedFailureModeId) && (
                    <SurveyEditFmecaFurtherInvestigations
                        isFmecaAvailable={this.props.isFmecaAvailable}
                        surveyId={this.props.surveyId}
                        failureModeId={this.state.selectedFailureModeId}
                        unitId={this.getUnitIdForMaintenanceMeasures()}
                        manifestationId={this.props.manifestationId}
                        header={this.getHeaderList(this.props.t('fmecaFurtherInvestigation.furtherInvestigation'))}
                    />
                )}
            </Tray>
        );
    }

    private getUnitIdForMaintenanceMeasures() {
        if (this.props.unitId) {
            return this.props.unitId;
        }
        if (!this.state.selectedFailureModeId) {
            return undefined;
        }

        return this.state.failureModes.find((i) => i.id === this.state.selectedFailureModeId)!.unit.id;
    }

    private async selectFailureMode(id?: string) {
        if (this.state.selectedFailureModeId === id) {
            await this.setStatePromised({ selectedFailureModeId: undefined, showEdit: false, showAdd: true });
            this.props.selectedFailureModeUpdate(undefined);
        } else {
            await this.setStatePromised({ selectedFailureModeId: id, showEdit: true, showAdd: false });
            this.props.selectedFailureModeUpdate(id);
        }
    }

    private async failureModeSaved(action: 'edit' | 'add', id?: string) {
        if (action === 'edit') {
            void showSavedToast(this.props.t);
        } else {
            await this.setStatePromised({ selectedFailureModeId: id });
        }
        await this.setStatePromised({ showEdit: false, showAdd: false, showCopy: false });
        await this.loadFailureModes();
        await this.loadUnitFailureModes();
    }

    private getHeaderFailureList() {
        if (!this.props.elementId && !this.props.unitId && !this.props.manifestationId) {
            return this.props.t('failureMode.allFailureModes');
        }
        return `${this.props.t('failureMode.failureModes')}: ${this.props.selectedName}`;
    }

    private getHeaderList(text: string) {
        if (!this.state.selectedFailureModeId) {
            if (!this.props.elementId && !this.props.unitId && !this.props.manifestationId) {
                return text;
            }
            return `${text}: ${this.props.selectedName}`;
        }
        const failureMode = this.state.failureModes.find((item: IFailureMode) => item.id === this.state.selectedFailureModeId);
        if (!failureMode) {
            return `${text} ${this.props.t('failureMode.failureMode')}: Failure mode not found.`;
        }
        return `${text} ${this.props.t('failureMode.failureMode')}: ${
            failureMode.defaultFailureMode.name ? failureMode.defaultFailureMode.name : failureMode.customName
        }`;
    }

    private async loadFailureModes() {
        const url = `/api/surveys/${this.props.surveyId}/failureModes`;

        const result = await fetchGet(url, {
            elementId: this.props.elementId,
            unitId: this.props.unitId,
            manifestationId: this.props.manifestationId,
        });
        await this.setStatePromised({ failureModes: result.json });
    }

    private async remove(id: string) {
        await this.setStatePromised({ showEdit: false, showAdd: false, selectedFailureModeId: undefined });
        await fetchDelete(`/api/surveys/${this.props.surveyId}/failureModes/${id}`);
        await this.loadFailureModes();
    }

    private async loadUnitFailureModes() {
        if (this.props.manifestationId) {
            const failureModesToCopy = await getUnitFailureModesToCopy(
                this.props.surveyId,
                this.props.elementId!,
                this.props.unitId!,
                this.state.failureModes,
            );
            await this.setStatePromised({ shouldShowCopyFailureModeButton: failureModesToCopy.length > 0, unitFailureModes: failureModesToCopy });
        } else {
            await this.setStatePromised({ shouldShowCopyFailureModeButton: false });
        }
    }

    private async showCopyFailureMode() {
        if (this.state.unitFailureModes.length === 1) {
            const unitFailureMode = this.state.unitFailureModes[0]!;
            await openApiFetchPost(
                '/api/surveys/{surveyId}/failureModes/copy',
                { surveyId: this.props.surveyId },
                { manifestationId: this.props.manifestationId!, failureModeId: unitFailureMode.id },
            );
            await this.failureModeSaved('edit');
        } else {
            this.setState({ showEdit: false, showCopy: true });
        }
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyMaintenance = translate()(SurveyMaintenanceComponent);
